import { c as _c } from "react/compiler-runtime";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, type ProfileDocument } from "@/api";
import { sellerKeys } from "@/api/hooks/use-sellers";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";

// Renders a seller's uploaded KYC document for moderation. The private bucket
// never exposes a public URL, so the bytes are fetched (with the admin bearer
// in the header) and turned into an object URL — PDFs in an <iframe>, images in
// an <img>, anything else falls back to open/download only.
type Kind = "pdf" | "image" | "other";
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i;
const detectKind = (mime: string, fileName?: string): Kind => {
  if (mime.includes("pdf") || fileName?.toLowerCase().endsWith(".pdf")) return "pdf";
  if (mime.startsWith("image/") || fileName && IMAGE_EXT.test(fileName)) return "image";
  return "other";
};
export function DocumentViewer(t0) {
  const $ = _c(56);
  const {
    userId,
    doc,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const [url, setUrl] = useState(null);
  const docId = doc?.id ?? "";
  let t1;
  if ($[0] !== docId || $[1] !== userId) {
    t1 = sellerKeys.document(userId, docId);
    $[0] = docId;
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== docId || $[4] !== userId) {
    t2 = () => api.sellers.profileDocumentFile(userId, docId);
    $[3] = docId;
    $[4] = userId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = !!doc;
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: blob,
    error
  } = useQuery(t4);
  let t5;
  if ($[10] !== blob || $[11] !== doc?.fileName) {
    t5 = blob ? detectKind(blob.type, doc?.fileName) : "other";
    $[10] = blob;
    $[11] = doc?.fileName;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const kind = t5;
  let t6;
  let t7;
  if ($[13] !== blob) {
    t6 = () => {
      if (!blob) {
        setUrl(null);
        return;
      }
      const objUrl = URL.createObjectURL(blob);
      setUrl(objUrl);
      return () => {
        URL.revokeObjectURL(objUrl);
      };
    };
    t7 = [blob];
    $[13] = blob;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  const fileName = doc?.fileName ?? doc?.name ?? "document";
  let t8;
  if ($[16] !== fileName || $[17] !== url) {
    t8 = () => {
      if (!url) {
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
    };
    $[16] = fileName;
    $[17] = url;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const download = t8;
  const t9 = !!doc;
  let t10;
  if ($[19] !== onClose) {
    t10 = open => !open && onClose();
    $[19] = onClose;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== fileName) {
    t11 = <DialogHeader><DialogTitle className="truncate pr-8">{fileName}</DialogTitle></DialogHeader>;
    $[21] = fileName;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== error || $[24] !== fileName || $[25] !== kind || $[26] !== t || $[27] !== url) {
    t12 = <div className="flex min-h-[60vh] items-center justify-center overflow-auto rounded-md border bg-muted/30">{error ? <p className="p-6 text-sm text-destructive">{t("sellers.docViewer.loadError")}</p> : !url ? <span className="inline-flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" />{t("sellers.docViewer.loading")}</span> : kind === "pdf" ? <iframe src={url} title={fileName} className="h-[70vh] w-full" /> : kind === "image" ? <img src={url} alt={fileName} className="max-h-[70vh] w-auto object-contain" /> : <p className="p-6 text-center text-sm text-muted-foreground">{t("sellers.docViewer.previewUnavailable")}</p>}</div>;
    $[23] = error;
    $[24] = fileName;
    $[25] = kind;
    $[26] = t;
    $[27] = url;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const t13 = !url;
  let t14;
  if ($[29] !== url) {
    t14 = () => url && window.open(url, "_blank", "noopener");
    $[29] = url;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ExternalLink className="size-4" />;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("sellers.docViewer.openInTab");
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t13 || $[35] !== t14 || $[36] !== t16) {
    t17 = <Button variant="outline" disabled={t13} onClick={t14}>{t15}{t16}</Button>;
    $[34] = t13;
    $[35] = t14;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const t18 = !url;
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Download className="size-4" />;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t) {
    t20 = t("sellers.docViewer.download");
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== download || $[42] !== t18 || $[43] !== t20) {
    t21 = <Button disabled={t18} onClick={download}>{t19}{t20}</Button>;
    $[41] = download;
    $[42] = t18;
    $[43] = t20;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t17 || $[46] !== t21) {
    t22 = <div className="flex justify-end gap-2">{t17}{t21}</div>;
    $[45] = t17;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t11 || $[49] !== t12 || $[50] !== t22) {
    t23 = <DialogContent className="max-w-3xl">{t11}{t12}{t22}</DialogContent>;
    $[48] = t11;
    $[49] = t12;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t10 || $[53] !== t23 || $[54] !== t9) {
    t24 = <Dialog open={t9} onOpenChange={t10}>{t23}</Dialog>;
    $[52] = t10;
    $[53] = t23;
    $[54] = t9;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
}